import React from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { dt, accentGradient, accentGradientDeep } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";

interface RfsActionBarProps {
  onCreateOffer: () => void;
  onBack: () => void;
  isOwnRequest: boolean;
  hasMyOffer?: boolean;
  /** True when the current user is the owner of this SR */
  isOwner?: boolean;
}

const RfsActionBar = ({ onCreateOffer, onBack, isOwnRequest, hasMyOffer, isOwner }: RfsActionBarProps) => {
  const { translate } = useTranslation(["serviceBoard"]);
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTabletOrPhone = useMediaQuery("(max-width:1200px)");

  // Owner view: only show "Back to My Requests" button (no Make Offer)
  // Provider view: show Make Offer (unless isOwnRequest) + "Back to Service Board"
  const showMakeOffer = !isOwnRequest && !isOwner;
  const backLabel = isOwner
    ? translate("serviceBoard:backToMyServiceRequests")
    : translate("serviceBoard:backToServiceBoard");

  // Phone + Tablet: fixed bottom bar
  if (isTabletOrPhone) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          backgroundColor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(8px)",
          borderTop: `1px solid ${dt.cardBorder}`,
          padding: isPhone ? "10px 16px" : "12px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          height: isPhone ? 64 : "auto",
        }}
      >
        {showMakeOffer && (
          <Button
            onClick={onCreateOffer}
            sx={{
              ...(hasMyOffer
                ? {
                    background: "transparent",
                    color: "#2AA198",
                    border: "1.5px solid #2AA198",
                    "&:hover": {
                      borderColor: "#239088",
                      color: "#239088",
                      backgroundColor: "rgba(42,161,152,0.06)",
                      transform: "translateY(-1px)",
                    },
                  }
                : {
                    background: accentGradient,
                    color: "#FFFFFF",
                    boxShadow: "0 4px 12px rgba(52, 152, 219, 0.3)",
                    "&:hover": {
                      background: accentGradientDeep,
                      transform: "translateY(-1px)",
                      boxShadow: "0 6px 16px rgba(52, 152, 219, 0.4)",
                    },
                  }),
              borderRadius: "24px",
              padding: "12px 28px",
              fontFamily: dt.font,
              fontSize: "14px",
              fontWeight: 700,
              textTransform: "none",
              flex: isPhone ? 1 : "none",
              transition: "all 0.2s ease",
            }}
            endIcon={<ChevronRightIcon sx={{ fontSize: "18px !important" }} />}
          >
            {hasMyOffer ? translate("serviceBoard:sendAnotherOffer") : translate("serviceBoard:makeOffer")}
          </Button>
        )}
        <Button
          onClick={onBack}
          sx={{
            backgroundColor: "transparent",
            border: `1.5px solid ${dt.cardBorder}`,
            color: dt.textSecondary,
            borderRadius: "24px",
            padding: "10px 20px",
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 500,
            textTransform: "none",
            flex: isPhone && (isOwnRequest || isOwner) ? 1 : "none",
            "&:hover": {
              borderColor: dt.blue,
              color: dt.blue,
              backgroundColor: dt.blueSoft,
            },
            transition: "all 0.15s",
          }}
          startIcon={<ArrowBackIcon sx={{ fontSize: "16px !important" }} />}
        >
          {backLabel}
        </Button>
      </Box>
    );
  }

  // Desktop: inline buttons in centre column
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
      {showMakeOffer && (
        <Button
          fullWidth
          onClick={onCreateOffer}
          sx={{
            ...(hasMyOffer
              ? {
                  background: "transparent",
                  color: "#2AA198",
                  border: "1.5px solid #2AA198",
                  "&:hover": {
                    borderColor: "#239088",
                    color: "#239088",
                    backgroundColor: "rgba(42,161,152,0.06)",
                    transform: "translateY(-1px)",
                  },
                }
              : {
                  background: accentGradient,
                  color: "#FFFFFF",
                  boxShadow: "0 4px 12px rgba(52, 152, 219, 0.3)",
                  animation: "ctaGlow 2.5s ease-in-out 1s 3",
                  "@keyframes ctaGlow": {
                    "0%, 100%": { boxShadow: "0 4px 12px rgba(52, 152, 219, 0.25)" },
                    "50%": { boxShadow: "0 4px 20px rgba(52, 152, 219, 0.45)" },
                  },
                  "&:hover": {
                    background: accentGradientDeep,
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 16px rgba(52, 152, 219, 0.4)",
                  },
                }),
            borderRadius: "24px",
            padding: "14px 28px",
            fontFamily: dt.font,
            fontSize: "14px",
            fontWeight: 700,
            textTransform: "none",
            transition: "all 0.2s ease",
          }}
          endIcon={<ChevronRightIcon sx={{ fontSize: "18px !important" }} />}
        >
          {hasMyOffer ? translate("serviceBoard:sendAnotherOffer") : translate("serviceBoard:makeOffer")}
        </Button>
      )}
      <Button
        fullWidth
        onClick={onBack}
        sx={{
          backgroundColor: "transparent",
          border: `1.5px solid ${dt.cardBorder}`,
          color: dt.textSecondary,
          borderRadius: "24px",
          padding: "10px 20px",
          fontFamily: dt.font,
          fontSize: "13px",
          fontWeight: 500,
          textTransform: "none",
          "&:hover": {
            borderColor: dt.blue,
            color: dt.blue,
            backgroundColor: dt.blueSoft,
          },
          transition: "all 0.15s",
        }}
        startIcon={<ArrowBackIcon sx={{ fontSize: "16px !important" }} />}
      >
        {backLabel}
      </Button>
    </Box>
  );
};

export default RfsActionBar;
